import { useEffect } from 'react';
import { type Control, useFormState } from 'react-hook-form';

import type { OrderFormStateChange } from '../OrderFormV3Context';
import type { OrderFormV3FieldValues } from '../OrderFormV3Context';

/**
 * Triggers: formState.isDirty / formState.isValid transitions (RHF subscription).
 * Writes:   nothing — relays { isDirty } to the OrderForm.tsx mount via the
 *           onFormStateChange prop so the drawer can gate its discard-changes
 *           confirmation.
 * Guards:   none — fires once on mount with the initial state and then on every
 *           transition, mirroring legacy exactly.
 * Legacy:   the legacy form's form-state change notifier.
 * Test:     __tests__/OrderFormV3Context.test.tsx ("onFormStateChange").
 *
 * Called by the effects/OrderFormV3Effects.tsx host, which runs every effect
 * module in one fixed, explicit order.
 */
export function useFormStateChangeNotifier({
  control,
  onFormStateChange,
}: {
  control: Control<OrderFormV3FieldValues>;
  onFormStateChange: (change: OrderFormStateChange) => void;
}): void {
  const { isDirty, isValid } = useFormState({ control });

  useEffect(() => {
    onFormStateChange({ isDirty });
    // Legacy deps verbatim ([isValid, isDirty]) — the callback prop is read
    // via closure, exactly as legacy reads it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid, isDirty]);
}
